// composables/gamification/service.ts
//
// ─────────────────────────────────────────────────────────────────────────────
//  SWAPPABLE GAMIFICATION SERVICE BOUNDARY (M4C-25)
// ─────────────────────────────────────────────────────────────────────────────
//
// This composable is the single seam between the gamification UI and its data.
// Every read now resolves live m4c-api data; the return types are the stable view
// models, so consuming components (M4C-20/21/28/29/34) bind the same shapes the
// mocks used to provide. Live endpoint map:
//
//   Authenticated-self reads (M4C-20/21, wired live in M4C-44) — m4c-api M4C-30/PR #197:
//     listConstellations()          →  GET gamification/overview            (constellations[] + per-member progress)
//     getConstellationDetail(id)    →  GET gamification/overview (+ actions) — found by id/slug; there is no per-id route
//     getProfileProgress()          →  GET gamification/overview            (summary block)
//     getRecentActivity(category)   →  GET gamification/activity[?category=]
//
//   Public per-member reads (M4C-34/42/43) — m4c-api PR #201:
//     getMemberStats(memberId)      →  GET community/members/:id/gamification
//     getMemberConstellations(id)   →  GET community/members/:id/gamification/constellations
//
// The self reads adapt the raw `gamification/overview` payload (a constellation
// list, each carrying an explicit per-member `progress` block, plus a `summary`)
// into the card / detail / progress view models. Disabled constellations are
// filtered out of normal member UI (M4C-30 product decision). Every endpoint
// returns the translatable `category` as a map ({ en, lt }) — or null for disabled
// constellations — which the data layer resolves to the active locale so the view
// models stay `category: string`.
//
// `getMemberStats` returns counts only (members-list badge); `getMemberConstellations`
// returns renderer-ready geometry + earned-star states for the profile rows.
//
// Reward feedback (M4C-29) is emitted through here too, but stays client-only:
// it delegates to `useRewardFeedbackStore` and is never sent to or read from an
// endpoint. `triggerMockRewardFeedback` is a dev-only `?reward=` demo until live
// action responses (M4C-35) wire real rewards through `emitRewardFeedback`.

import { useRewardFeedbackStore } from '@/stores/rewardFeedback';
import type {
  ActivityCategory,
  ApiGamificationActions,
  ApiGamificationActivity,
  ApiGamificationOverview,
  ConstellationDetail,
  ConstellationOverviewCard,
  MemberConstellationRow,
  MemberGamificationStats,
  ProfileProgressSummary,
  RecentActivity,
  RewardFeedbackEvent,
  RewardFeedbackInput,
} from '@/types/gamification';
import {
  buildConstellationDetailFromApi,
  buildOverviewCardFromApi,
  localizeMemberConstellationRow,
  localizeMemberStats,
  mapAction,
  mapActivityRow,
  mapOverviewSummary,
  slugifyConstellation,
} from './adapters';
import { mockStarBornFeedback, mockToastFeedback } from './mocks';

/** Demo scenarios for `triggerMockRewardFeedback` (M4C-29). */
export type MockRewardScenario = 'star' | 'toasts' | 'all';

export function useGamificationService() {
  const rewardFeedback = useRewardFeedbackStore();
  // Active locale for resolving translatable `category` maps on the live member
  // reads (M4C-43). Captured here (setup context) and read at fetch time.
  const { locale } = useI18n();

  /** Activity sky overview cards, in sky order (M4C-20). Disabled constellations are hidden. */
  async function listConstellations(): Promise<ConstellationOverviewCard[]> {
    const res = await $fetch<{ data: ApiGamificationOverview }>('gamification/overview');
    return res.data.constellations
      .filter((c) => c.is_enabled)
      .map((c) => buildOverviewCardFromApi(c, locale.value))
      .sort((a, b) => a.position - b.position);
  }

  /**
   * Selected-constellation detail (M4C-21). Accepts the route slug or the raw id.
   * Derived from the overview payload (there is no per-id route) joined with the
   * actions catalog. Throws a 404 for an unknown/disabled constellation — the
   * error-state path.
   */
  async function getConstellationDetail(idOrSlug: string): Promise<ConstellationDetail> {
    const [overview, actionsRes] = await Promise.all([
      $fetch<{ data: ApiGamificationOverview }>('gamification/overview'),
      $fetch<{ data: ApiGamificationActions }>('gamification/actions'),
    ]);
    const key = idOrSlug.toLowerCase();
    const api = overview.data.constellations.find(
      (c) => c.is_enabled && (c.id === idOrSlug || slugifyConstellation(c.name) === key),
    );
    if (!api) {
      throw createError({
        statusCode: 404,
        statusMessage: `Constellation "${idOrSlug}" not found`,
      });
    }
    return buildConstellationDetailFromApi(api, actionsRes.data.actions.map(mapAction), locale.value);
  }

  /** Profile progress summary (M4C-20), mapped from the overview summary block. */
  async function getProfileProgress(): Promise<ProfileProgressSummary> {
    const res = await $fetch<{ data: ApiGamificationOverview }>('gamification/overview');
    return mapOverviewSummary(res.data);
  }

  /**
   * Recent activity (M4C-21). Optional category filters the feed. Rows stay empty
   * until the crediting/drift runtime (M4C-35/36) produces entries.
   */
  async function getRecentActivity(category?: ActivityCategory): Promise<RecentActivity> {
    const res = await $fetch<{ data: ApiGamificationActivity }>('gamification/activity', {
      params: category ? { category } : {},
    });
    return {
      rows: res.data.activities.map(mapActivityRow),
      counts: res.data.counts,
    };
  }

  /**
   * Public gamification stat counts for a member (M4C-34) — drives the
   * members-list avatar badge + tooltip. Counts only; for the geometry-bearing
   * profile rows use {@link getMemberConstellations}.
   */
  async function getMemberStats(memberId: string): Promise<MemberGamificationStats> {
    const res = await $fetch<{ data: MemberGamificationStats }>(
      `community/members/${memberId}/gamification`,
    );
    return localizeMemberStats(res.data, locale.value);
  }

  /**
   * A member's non-empty constellations with renderer-ready geometry +
   * earned-star states (M4C-34), for the "Activity sky" rows on their profile.
   * Joins the public per-member progress with the constellation catalog and
   * derives star states from `stars_earned` — so the component hardcodes no
   * geometry or earning rules. Constellations with zero earned stars are omitted
   * (the profile shows only what a member has started), sorted by sky position.
   */
  async function getMemberConstellations(memberId: string): Promise<MemberConstellationRow[]> {
    const res = await $fetch<{ data: MemberConstellationRow[] }>(
      `community/members/${memberId}/gamification/constellations`,
    );
    return res.data.map((row) => localizeMemberConstellationRow(row, locale.value));
  }

  /**
   * Emit an ephemeral reward feedback event (M4C-29) — star-born modal / toast.
   * Client-only and transient; never persisted. Returns the stored event.
   */
  function emitRewardFeedback(event: RewardFeedbackInput): RewardFeedbackEvent {
    return rewardFeedback.emit(event);
  }

  /**
   * Fire sample reward feedback for development/demo (M4C-29) — a client-only
   * `?reward=` trigger that stands in until live action responses (M4C-35) wire
   * real rewards through `emitRewardFeedback`. Emits the same events the live path
   * will, so the modal/toasts render exactly as in production. The star-born modal
   * degrades gracefully when its mock constellation id has no live geometry.
   */
  function triggerMockRewardFeedback(scenario: MockRewardScenario = 'all'): void {
    if (scenario === 'star' || scenario === 'all') {
      rewardFeedback.emit(mockStarBornFeedback);
    }
    if (scenario === 'toasts' || scenario === 'all') {
      mockToastFeedback.forEach((event) => rewardFeedback.emit(event));
    }
  }

  return {
    listConstellations,
    getConstellationDetail,
    getProfileProgress,
    getRecentActivity,
    getMemberStats,
    getMemberConstellations,
    emitRewardFeedback,
    triggerMockRewardFeedback,
  };
}
